import {createResource, getResource, updateResource, OkDocument, ErrorDocument} from "./common"
import {Result} from "neverthrow"

export type BillpayOrgSettings = {
	type: "orgSettings"
	id: string
	attributes: {
		createdAt: string
		updatedAt: string
		enableCustomerLevelCategories: boolean
		bankingTermsId: string
		autoLinkAccounts: boolean
	}
	relationships: {
		org: {
			data: {
				type: "org"
				id: string
			}
		}
	}
}

export async function findBillpayOrgSettings(
	accessToken: string,
	offset: number,
	limit: number,
	orgIds?: string[]
): Promise<Result<OkDocument<Array<BillpayOrgSettings>>, ErrorDocument>> {
	const query: any = {
		page: {
			limit,
			offset,
		},
	}

	if (orgIds && orgIds.length > 0) {
		const parsedIds = orgIds.map((id) => parseInt(id, 10)).filter((id) => !isNaN(id) && id > 0)
		if (parsedIds.length > 0) {
			query.filter = {orgIds: parsedIds}
		}
	}

	return await getResource<Array<BillpayOrgSettings>>("orgs/billpay-settings", accessToken, query)
}

export async function getBillpayOrgSettings(
	accessToken: string,
	id: string
): Promise<Result<OkDocument<BillpayOrgSettings>, ErrorDocument>> {
	return await getResource<BillpayOrgSettings>(`orgs/billpay-settings/${id}`, accessToken)
}

export async function getBillpayOrgSettingsByOrgId(
	accessToken: string,
	orgId: string
): Promise<Result<OkDocument<BillpayOrgSettings>, ErrorDocument>> {
	return await getResource<BillpayOrgSettings>(`orgs/${orgId}/billpay-settings`, accessToken)
}

export async function createBillpayOrgSettings(
	accessToken: string,
	orgId: string,
	enableCustomerLevelCategories: boolean,
	bankingTermsId: string,
	autoLinkAccounts: boolean,
	idempotencyKey: string
): Promise<Result<BillpayOrgSettings, ErrorDocument>> {
	const result = await createResource<BillpayOrgSettings>("orgs/billpay-settings", accessToken, {
		type: "orgSettings",
		attributes: {
			idempotencyKey,
			enableCustomerLevelCategories,
			bankingTermsId,
			autoLinkAccounts,
		},
		relationships: {
			org: {
				data: {
					type: "org",
					id: orgId,
				},
			},
		},
	})
	return result.map((v) => v.data)
}

export async function updateBillpayOrgSettings(
	accessToken: string,
	orgId: string,
	enableCustomerLevelCategories: boolean | undefined,
	autoLinkAccounts: boolean | undefined,
	idempotencyKey: string
): Promise<Result<BillpayOrgSettings, ErrorDocument>> {
	const attributes: any = {idempotencyKey}
	if (enableCustomerLevelCategories !== undefined) {
		attributes.enableCustomerLevelCategories = enableCustomerLevelCategories
	}
	if (autoLinkAccounts !== undefined) {
		attributes.autoLinkAccounts = autoLinkAccounts
	}

	const result = await updateResource<BillpayOrgSettings>("orgs/billpay-settings", accessToken, {
		type: "orgSettings",
		attributes,
		relationships: {
			org: {
				data: {
					type: "org",
					id: orgId,
				},
			},
		},
	})
	return result.map((v) => v.data)
}
